import { useEffect, useState } from 'react';

import { isDarkModeApplied } from '#src/app/shared/utils/dom';

/**
 * Tracks the `.dark` class on `<html>` — see {@link isDarkModeApplied}.
 * Returns `false` on the server and on the first client render, then updates
 * once mounted and stays in sync with future class changes (e.g. a live OS
 * `prefers-color-scheme` change).
 *
 * @example
 * ```tsx
 * const isDark = useIsDarkMode();
 * ```
 */
export const useIsDarkMode = (): boolean => useDarkModeClass(false);

/**
 * Like {@link useIsDarkMode}, but reads the `.dark` class on the first client render too, so it
 * never reports light before correcting itself. Only for values that are not rendered into SSR
 * markup (e.g. options handed to a third-party iframe), since the server always sees `false`.
 */
export const useLiveIsDarkMode = (): boolean => useDarkModeClass(isDarkModeApplied);

function useDarkModeClass(initialState: boolean | (() => boolean)): boolean {
	const [isDark, setIsDark] = useState(initialState);

	useEffect(() => {
		const update = (): void => setIsDark(isDarkModeApplied());

		update();
		const observer = new MutationObserver(update);
		observer.observe(document.documentElement, { attributeFilter: ['class'] });
		return () => observer.disconnect();
	}, []);

	return isDark;
}
